import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { useState } from 'react';
import { createShareableSnapshot, getShareableUrl } from '../utils/sharing';
import { toast } from 'react-hot-toast';

export const ShareDialog = ({ isOpen, onClose, title, blocks, userId }) => {
  const [shareUrl, setShareUrl] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGenerated, setIsGenerated] = useState(false);

  const handleGenerateLink = async () => {
    if (!userId) {
      toast.error('Please log in to share documents');
      return;
    }

    setIsGenerating(true);
    try {
      const shareId = await createShareableSnapshot(title, blocks, userId);
      const url = getShareableUrl(shareId);
      setShareUrl(url);
      setIsGenerated(true);
      toast.success('Share link created!');
    } catch (error) {
      console.error('Error creating share link:', error);
      toast.error('Failed to create share link');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied to clipboard!');
  };

  const handleClose = () => {
    setShareUrl('');
    setIsGenerated(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50 text-white">
      <DialogBackdrop className="fixed inset-0 bg-black/70" />
      <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
        <DialogPanel className="border border-white/20 w-full max-w-[500px] rounded-[30px] bg-[#4A4A4A]/10 backdrop-blur-sm p-6">
          <DialogTitle className="font-bold text-xl mb-4">
            Share Note
          </DialogTitle>

          <p className="text-gray-400 text-sm mb-6">
            Create a shareable link. Anyone with the link can view this note and make their own copy to edit.
          </p>

          {!isGenerated ? (
            <button
              onClick={handleGenerateLink}
              disabled={isGenerating}
              className="w-full py-3 bg-[#7BCDBA] text-black font-semibold rounded-xl hover:bg-[#6bbdaa] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGenerating ? 'Generating...' : 'Generate Share Link'}
            </button>
          ) : (
            <>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-4">
                <p className="text-sm text-gray-400 mb-2">Share Link:</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 bg-transparent text-white text-sm outline-none select-all"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    title="Copy link"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                  </button>
                </div>
              </div>

              <button
                onClick={handleCopyLink}
                className="w-full py-3 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors mb-2"
              >
                Copy Link
              </button>
            </>
          )}

          <button
            onClick={handleClose}
            className="w-full py-2 text-gray-400 hover:text-white transition-colors text-sm mt-4"
          >
            {isGenerated ? 'Done' : 'Cancel'}
          </button>
        </DialogPanel>
      </div>
    </Dialog>
  );
};
